/** Resolves once `selector` matches an element in the document, or when
 * `timeoutMs` elapses — whichever happens first.
 *
 * Written for the dataset detail page's feature tour: the composition plot
 * tabs render lazily, so a tour step attached to a tab panel has to wait for
 * that panel to appear after the tour clicks its tab.
 *
 * Resolves with `null` on timeout rather than rejecting. A Shepherd step whose
 * anchor never materialises is still shown (centred on screen); a rejection
 * would leave the tour stuck on the previous step instead.
 *
 * Note: the timeout is checked on `pollMs` boundaries. The effective wait time
 * rounds up to the next multiple of `pollMs` and is never shorter than one full
 * `pollMs` interval, even if `timeoutMs` is less than `pollMs`.
 */
export function waitForElement(
  selector: string,
  timeoutMs: number,
  pollMs: number = 50
): Promise<Element | null> {
  const existing = document.querySelector(selector);
  if (existing) {
    return Promise.resolve(existing);
  }
  return new Promise<Element | null>(resolve => {
    let waited = 0;
    const timer = setInterval(() => {
      waited += pollMs;
      const found = document.querySelector(selector);
      if (found || waited >= timeoutMs) {
        clearInterval(timer);
        resolve(found);
      }
    }, pollMs);
  });
}
